import { ChevronDown, Folder } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useI18n } from "@/i18n"
import type { Project } from "@/types/api"

const NO_PROJECT = ""

interface ProjectPickerProps {
  projects: Project[]
  selectedId: string | null
  onSelect: (projectId: string | null) => void
}

export function ProjectPicker({ projects, selectedId, onSelect }: ProjectPickerProps) {
  const { t } = useI18n()
  if (projects.length === 0) return null

  const selected = projects.find((project) => project.id === selectedId)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("project.select")}
          title={selected ? t("project.current", { name: selected.name }) : t("project.add")}
          className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-fg2 transition-colors hover:border-orange/50 hover:bg-hover hover:text-foreground"
        >
          <Folder className="size-3.5 text-orange-press" />
          <span className="max-w-48 truncate">{selected ? selected.name : t("project.none")}</span>
          <ChevronDown className="size-3" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="center" className="max-h-80 w-60 overflow-y-auto">
        <DropdownMenuLabel className="text-fg3">{t("project.label")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={selectedId ?? NO_PROJECT}
          onValueChange={(value) => onSelect(value === NO_PROJECT ? null : value)}
        >
          <DropdownMenuRadioItem value={NO_PROJECT} className="cursor-pointer">
            <span className="truncate">{t("project.none")}</span>
          </DropdownMenuRadioItem>
          {projects.map((project) => (
            <DropdownMenuRadioItem key={project.id} value={project.id} className="cursor-pointer">
              <span className="truncate">{project.name}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
